import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, ShieldCheck, Truck } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";
import ProductCard from "../components/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1627899590758-9a41d43d49e6?crop=entropy&cs=srgb&fm=jpg&q=85";
const COL_IMG_1 = "https://images.unsplash.com/photo-1687180497278-ca4d736ecc99?crop=entropy&cs=srgb&fm=jpg&q=85";
const COL_IMG_2 = "https://images.unsplash.com/photo-1777159744473-7c34051f33d8?crop=entropy&cs=srgb&fm=jpg&q=85";
const COL_IMG_3 = "https://images.unsplash.com/photo-1642976240849-d5f7dac74b23?crop=entropy&cs=srgb&fm=jpg&q=85";

const TESTIMONIALS_FALLBACK = [
  { name: "Andrei Popescu", role: "Colecționar Privat, București", quote: "Standardul Johny's Antique Shop este de neegalat. Am achiziționat trei piese, fiecare însoțită de o documentare impecabilă." },
  { name: "Ileana Marinescu", role: "Curator Galerie, Cluj", quote: "O sursă de încredere pentru rarități autentice. Echipa este profesionistă și cu o cunoaștere remarcabilă a istoriei." },
  { name: "Vlad Constantin", role: "Designer de Interior, Sibiu", quote: "Selecția lor de mobilier antic transformă orice proiect într-o experiență memorabilă." },
];

const CATEGORIES = [
  { name: "Mobilier Antic", img: COL_IMG_1 },
  { name: "Picturi Rare", img: COL_IMG_2 },
  { name: "Ceasuri Vintage", img: HERO_IMG },
  { name: "Bijuterii de Lux", img: COL_IMG_3 },
];

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [rare, setRare] = useState([]);
  const TESTIMONIALS = (settings.testimonials && settings.testimonials.length > 0) ? settings.testimonials : TESTIMONIALS_FALLBACK;

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { featured: true, limit: 6 } }),
      api.get("/products", { params: { trending: true, limit: 4 } }),
      api.get("/products", { params: { rare: true, limit: 3 } }),
    ]).then(([f, t, r]) => {
      setFeatured(f.data); setTrending(t.data); setRare(r.data);
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 pt-40 w-full">
          <div className="max-w-2xl fade-up">
            <p className="label-luxe text-[#D4AF37] mb-6">{settings.brand_tagline || "Casa de Antichități · Estd. 1987"}</p>
            <h1 className="h-display text-5xl sm:text-7xl lg:text-8xl text-[#F8F4E6] mb-8">
              {settings.hero_title_main || "Moștenirea"}<br /><em className="gold-gradient-text not-italic">{settings.hero_title_accent || "Timpului"}</em>
            </h1>
            <p className="text-lg text-[#A39E8A] font-light max-w-xl leading-relaxed mb-10">
              {settings.hero_subtitle || "Descoperă obiecte de artă și antichități inestimabile, atent selecționate pentru colecționarii rafinați. Fiecare piesă povestește o istorie irepetabilă."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/shop" className="btn-gold" data-testid="hero-cta-shop">
                {settings.hero_cta_primary || "Descoperă Colecția"} <ArrowRight size={14} strokeWidth={1.6} />
              </Link>
              <Link to="/about" className="btn-ghost" data-testid="hero-cta-about">{settings.hero_cta_secondary || "Povestea Casei"}</Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A39E8A] text-[10px] uppercase tracking-[0.4em]">Derulează</div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#D4AF37]/10 py-10 bg-[#070707]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
          {[
            { Icon: Award, title: "Autenticitate Garantată", text: "Fiecare piesă vine cu certificat de origine și expertiză." },
            { Icon: ShieldCheck, title: "Tranzacții Securizate", text: "Plăți criptate și asigurare integrată pentru fiecare obiect." },
            { Icon: Truck, title: "Livrare Curatorială", text: "Transport asigurat în toată Europa, ambalat manual." },
          ].map(({ Icon, title, text }, i) => (
            <div key={i} className="flex flex-col items-center" data-testid={`trust-${i}`}>
              <Icon size={28} strokeWidth={1.2} className="text-[#D4AF37] mb-4" />
              <h4 className="font-serif text-xl text-[#F8F4E6] mb-2">{title}</h4>
              <p className="text-sm text-[#A39E8A]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="label-luxe text-[#D4AF37] mb-3">Lumea Noastră</p>
              <h2 className="h-display text-4xl sm:text-5xl text-[#F8F4E6]">Categorii Reprezentative</h2>
            </div>
            <Link to="/shop" className="text-[#D4AF37] text-[11px] uppercase tracking-[0.25em] border-b border-[#D4AF37] pb-1">Toate Categoriile</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {CATEGORIES.map((c, i) => (
              <Link key={c.name} to={`/shop?category=${encodeURIComponent(c.name)}`} className="group relative aspect-[3/4] overflow-hidden bg-[#0F0F0F]" data-testid={`cat-${i}`}>
                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="label-luxe text-[#D4AF37] mb-2">Categorie</p>
                  <h3 className="font-serif text-2xl text-[#F8F4E6] group-hover:text-[#D4AF37] transition">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 bg-[#070707]" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="label-luxe text-[#D4AF37] mb-3">Selecție Curatorială</p>
            <h2 className="h-display text-4xl sm:text-5xl lg:text-6xl text-[#F8F4E6]">Colecții Reprezentative</h2>
            <span className="luxe-divider mt-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
          </div>
        </div>
      </section>

      {/* RARE COLLECTIBLES BIG SHOWCASE */}
      {rare[0] && (
        <section className="py-24" data-testid="rare-showcase">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
            <Link to={`/products/${rare[0].id}`} className="block aspect-[4/5] overflow-hidden bg-[#0F0F0F]">
              <img src={rare[0].images?.[0]} alt={rare[0].name} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
            </Link>
            <div>
              <p className="label-luxe text-[#D4AF37] mb-4">Artefact Rar</p>
              <h2 className="h-display text-4xl sm:text-6xl text-[#F8F4E6] mb-6">{rare[0].name}</h2>
              <p className="text-[#A39E8A] leading-relaxed mb-8">{rare[0].description}</p>
              <div className="flex items-center gap-6 mb-10">
                <span className="font-serif text-3xl text-[#D4AF37]">{new Intl.NumberFormat("ro-RO", { style: "currency", currency: rare[0].currency }).format(rare[0].price)}</span>
                <span className="label-luxe">{rare[0].origin}</span>
              </div>
              <Link to={`/products/${rare[0].id}`} className="btn-gold">Vezi Detalii <ArrowRight size={14} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* TRENDING */}
      <section className="py-24 bg-[#070707]" data-testid="trending-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="label-luxe text-[#D4AF37] mb-3">Cele Mai Râvnite</p>
              <h2 className="h-display text-4xl sm:text-5xl text-[#F8F4E6]">Licitații Recente</h2>
            </div>
            <Link to="/shop?trending=true" className="text-[#D4AF37] text-[11px] uppercase tracking-[0.25em] border-b border-[#D4AF37] pb-1">Vezi Toate</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trending.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32" data-testid="testimonials-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <p className="label-luxe text-[#D4AF37] mb-4">Aprecierea Colecționarilor</p>
          <h2 className="h-display text-4xl sm:text-5xl text-[#F8F4E6] mb-16">Vocile Clienților Noștri</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border-t border-[#D4AF37]/20 pt-8 text-left" data-testid={`testimonial-${i}`}>
                <p className="font-serif text-2xl text-[#F8F4E6] leading-snug italic mb-6">“{t.quote}”</p>
                <p className="label-luxe text-[#D4AF37]">{t.name}</p>
                <p className="text-xs text-[#A39E8A] mt-1">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden border-y border-[#D4AF37]/15">
        <img src={COL_IMG_2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#050505]/80" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="label-luxe text-[#D4AF37] mb-4">Acces în Avanpremieră</p>
          <h2 className="h-display text-4xl sm:text-6xl text-[#F8F4E6] mb-6">Devino Colecționar Privilegiat</h2>
          <p className="text-[#A39E8A] mb-10">Înscrie-te în clubul Johny's Antique Shop și primește notificări private la fiecare nouă achiziție rare.</p>
          <Link to="/contact" className="btn-gold" data-testid="cta-contact">Solicită Invitație</Link>
        </div>
      </section>
    </main>
  );
}
